/**
 * web-client-app D4 — the studio website's page addresses, one table for both
 * sides: the API's site dispatcher decides page-or-404 from it, and the
 * website's router (apps/webapp/src/web/paths.ts) builds its links from it.
 *
 * The name part of a service or event address is cosmetic: built from the
 * name, ignored on read. A missing or outdated one still opens the page, which
 * names the current address as canonical, so a rename never breaks a link.
 * Pure and browser-safe.
 */

export type WebPage =
  | { readonly page: 'home' }
  | { readonly page: 'events' }
  | { readonly page: 'services' }
  | { readonly page: 'service'; readonly id: string; readonly nameSlug: string | null }
  | { readonly page: 'event'; readonly id: string; readonly nameSlug: string | null }
  // shared-client-screens D5: a series, a package of dates sold as one booking.
  | { readonly page: 'series'; readonly id: string; readonly nameSlug: string | null }
  // web-account-pages D8, D10: the studio's About page, and the app's Coming soon pages Invite and Waitlists.
  | { readonly page: 'about' }
  | { readonly page: 'invite' }
  | { readonly page: 'waitlists' }
  // web-sign-in-checkout D7: the visitor's own pages, never indexed or cached.
  | { readonly page: 'checkout' }
  | { readonly page: 'booked'; readonly bookingId: string }
  | { readonly page: 'pay'; readonly bookingId: string }
  // web-account-pages D1: the signed-in client's account pages, and a form the studio sent.
  | { readonly page: 'bookings' }
  | { readonly page: 'booking'; readonly bookingId: string }
  // web-account-pages D11: the Complete booking step of a booking the studio made that waits on the client.
  | { readonly page: 'complete'; readonly bookingId: string }
  | { readonly page: 'profile' }
  | { readonly page: 'form-request'; readonly requestToken: string }
  // studio-shop D4: the shop's bag, its checkout, and an order's page (also the thank-you page).
  | { readonly page: 'shop-bag' }
  | { readonly page: 'shop-checkout' }
  | { readonly page: 'shop-order'; readonly orderId: string }
  // form-embed D3: a general form the studio put on its website, by its website name.
  | { readonly page: 'site-form'; readonly key: string };

type PrivatePage =
  | 'checkout' | 'booked' | 'pay' | 'bookings' | 'booking' | 'complete' | 'profile' | 'form-request'
  | 'shop-bag' | 'shop-checkout' | 'shop-order';
const PRIVATE_PAGES: ReadonlySet<WebPage['page']> = new Set<PrivatePage>([
  'checkout', 'booked', 'pay', 'bookings', 'booking', 'complete', 'profile', 'form-request', 'shop-bag', 'shop-checkout', 'shop-order',
]);

/** A page that belongs to one visitor. */
export type PrivateWebPage = Extract<WebPage, { page: PrivatePage }>;

/** The pages that belong to one visitor: kept out of search, the sitemap and every cache. */
export function isPrivateWebPage(page: WebPage): page is PrivateWebPage {
  return PRIVATE_PAGES.has(page.page);
}

/** studio-shop D4: one of the shop's own pages. */
export type ShopWebPage = Extract<WebPage, { page: 'shop-bag' | 'shop-checkout' | 'shop-order' }>;

export function isShopWebPage(page: WebPage): page is ShopWebPage {
  return page.page === 'shop-bag' || page.page === 'shop-checkout' || page.page === 'shop-order';
}

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const SLUG_RE = /^[a-z0-9-]{1,120}$/;
/** A form request's capability token (the Mini App's `/form-request/:requestToken`). */
const TOKEN_RE = /^[A-Za-z0-9_-]{16,128}$/;

/** form-embed D1: a form's website name, kebab-case, 3 to 40 characters (the `Form_site_key_shape` CHECK). */
const SITE_FORM_KEY_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export function isSiteFormKey(key: string): boolean {
  return key.length >= 3 && key.length <= 40 && SITE_FORM_KEY_RE.test(key);
}

/** The cosmetic name part: lowercase ascii words joined by hyphens. */
export function webNameSlug(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '');
  return slug;
}

/** A service id's shape; a service's link form never has it, so a link can't be mistaken for an id. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SERVICE_WEB_NAME_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export function isUuidShaped(value: string): boolean {
  return UUID_RE.test(value);
}

/**
 * session-own-link D1: a service's name in link form (the database keeps it as `Service.webName`, through
 * `telify_web_name`, which mirrors `webNameSlug`). Null when the name has no letter or number, or would read as an id.
 */
export function serviceWebName(name: string): string | null {
  const slug = webNameSlug(name);
  return slug && !UUID_RE.test(slug) ? slug : null;
}

/** session-own-link D4: whether an address segment can be a service's link form (up to `webNameSlug`'s 80). */
export function isServiceWebName(value: string): boolean {
  return value.length <= 80 && SERVICE_WEB_NAME_RE.test(value) && !UUID_RE.test(value);
}

export function webServicePath(id: string, name: string): string {
  const slug = webNameSlug(name);
  return slug ? `/services/${id}/${slug}` : `/services/${id}`;
}

export function webEventPath(id: string, name: string): string {
  const slug = webNameSlug(name);
  return slug ? `/events/${id}/${slug}` : `/events/${id}`;
}

/** A series has its own address: `/events/series/<id>` would read as an event id. */
export function webSeriesPath(id: string, name: string): string {
  const slug = webNameSlug(name);
  return slug ? `/series/${id}/${slug}` : `/series/${id}`;
}

/** Match a request path (no query) to a website page, or null for anything else. */
export function matchWebPage(rawPath: string): WebPage | null {
  const q = rawPath.search(/[?#]/);
  let path = q >= 0 ? rawPath.slice(0, q) : rawPath;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (path === '' || path === '/') return { page: 'home' };
  if (path === '/events') return { page: 'events' };
  if (path === '/services') return { page: 'services' };
  if (path === '/about') return { page: 'about' };
  if (path === '/invite') return { page: 'invite' };
  if (path === '/waitlists') return { page: 'waitlists' };
  if (path === '/checkout') return { page: 'checkout' };
  if (path === '/account/bookings') return { page: 'bookings' };
  if (path === '/account/profile') return { page: 'profile' };
  if (path === '/shop/bag') return { page: 'shop-bag' };
  if (path === '/shop/checkout') return { page: 'shop-checkout' };
  const parts = path.split('/').slice(1);
  if (parts[0] === 'shop' && parts[1] === 'orders' && parts.length === 3 && ID_RE.test(parts[2]!)) return { page: 'shop-order', orderId: parts[2]! };
  if (parts[0] === 'booked' && parts.length === 2 && ID_RE.test(parts[1]!)) return { page: 'booked', bookingId: parts[1]! };
  if (parts[0] === 'account' && parts[1] === 'bookings' && ID_RE.test(parts[2] ?? '')) {
    if (parts.length === 3) return { page: 'booking', bookingId: parts[2]! };
    if (parts.length === 4 && parts[3] === 'pay') return { page: 'pay', bookingId: parts[2]! };
    if (parts.length === 4 && parts[3] === 'complete') return { page: 'complete', bookingId: parts[2]! };
  }
  if (parts[0] === 'form-request' && parts.length === 2 && TOKEN_RE.test(parts[1]!)) return { page: 'form-request', requestToken: parts[1]! };
  if (parts[0] === 'forms' && parts.length === 2 && isSiteFormKey(parts[1]!)) return { page: 'site-form', key: parts[1]! };
  const kind = parts[0] === 'services' ? 'service' : parts[0] === 'events' ? 'event' : parts[0] === 'series' ? 'series' : null;
  if (kind && (parts.length === 2 || parts.length === 3)) {
    const id = parts[1] ?? '';
    const nameSlug = parts[2] ?? null;
    // session-own-link D4: a service is also reached by its link form, which can run past an id's 64.
    if (!ID_RE.test(id) && !(kind === 'service' && isServiceWebName(id))) return null;
    if (nameSlug !== null && !SLUG_RE.test(nameSlug)) return null;
    return { page: kind, id, nameSlug };
  }
  return null;
}

/** The files a studio address serves beside its pages. */
export const WEB_SITE_FILES = ['/sitemap.xml', '/robots.txt'] as const;
